import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ArrowRight, ChevronRight, Phone, Loader2, Clock, User } from "lucide-react";

/**
 * Landing page SEO auto pour une catégorie de blog.
 * URL: /blog/categorie/:slug
 */
export default function BlogCategoryPage() {
  const { slug } = useParams();
  const { settings: s } = useSettings();
  const [category, setCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      api.get(`/blog-categories/by-slug/${slug}`).catch(() => ({ data: null })),
      api.get("/blog", { params: { published: true } }).catch(() => ({ data: [] })),
    ]).then(([catRes, postsRes]) => {
      if (cancelled) return;
      if (!catRes.data) { setNotFound(true); setLoading(false); return; }
      setCategory(catRes.data);
      const cat = catRes.data;
      const matchKeys = [cat.slug, cat.name].map(x => (x || "").toLowerCase()).filter(Boolean);
      setPosts((postsRes.data || []).filter(p => matchKeys.includes((p.category || "").toLowerCase())));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug]);

  const baseUrl = useMemo(() => (typeof window !== "undefined" ? window.location.origin : ""), []);
  const canonical = `${baseUrl}/blog/categorie/${slug}`;
  const meta_title = category?.meta_title || (category ? `${category.name} — Blog ${s?.brand?.name || ""}`.trim() : "");
  const meta_description = category?.meta_description || category?.description ||
    `Articles ${category?.name || ""} — ${posts.length} article${posts.length > 1 ? "s" : ""}.`;

  const jsonLd = category ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": category.h1 || category.name,
    "description": meta_description,
    "url": canonical,
    "mainEntity": {
      "@type": "Blog",
      "blogPost": posts.slice(0, 20).map(p => ({
        "@type": "BlogPosting",
        "headline": p.title,
        "datePublished": p.published_at || p.created_at,
        "image": p.cover_image,
        "url": `${baseUrl}/blog/${p.slug || p.id}`,
        "author": p.author ? { "@type": "Person", "name": p.author } : undefined,
      })),
    },
  } : null;

  useDocumentMeta({
    title: meta_title,
    description: meta_description,
    image: category?.cover_image || posts[0]?.cover_image,
    type: "website",
    canonical,
    jsonLd,
  });

  if (notFound) return <Navigate to="/blog" replace />;
  if (loading || !category) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[color:var(--brand-sky)]" />
      </div>
    );
  }

  return (
    <main className="min-h-screen" data-testid="blog-category-page">
      <nav className="bg-[hsl(var(--muted))]/30 border-b border-[hsl(var(--border))]">
        <div className="container mx-auto px-4 py-3 text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:underline">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:underline">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="font-semibold text-[color:var(--brand-navy)]">{category.name}</span>
        </div>
      </nav>

      <section
        className="relative py-16 md:py-24 overflow-hidden"
        style={category.cover_image ? {
          backgroundImage: `linear-gradient(rgba(11,34,66,0.85), rgba(11,34,66,0.75)), url(${category.cover_image})`,
          backgroundSize: "cover", backgroundPosition: "center", color: "white",
        } : { background: `linear-gradient(135deg, ${category.color}15, ${category.color}05)` }}
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <Badge className="mb-3 bg-white/20 text-white border-0 text-[10px] uppercase tracking-widest font-bold">
            Catégorie · {posts.length} article{posts.length > 1 ? "s" : ""}
          </Badge>
          <h1 className={`font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 ${category.cover_image ? "text-white" : "text-[color:var(--brand-navy)]"}`} data-testid="blog-category-h1">
            {category.h1 || category.name}
          </h1>
          {category.description && (
            <p className={`text-lg md:text-xl ${category.cover_image ? "text-white/90" : "text-[hsl(var(--muted-foreground))]"} max-w-2xl`}>
              {category.description}
            </p>
          )}
        </div>
      </section>

      {category.intro_text && (
        <section className="py-10 md:py-14 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <article className="prose prose-slate prose-base md:prose-lg max-w-none prose-headings:font-display prose-headings:text-[color:var(--brand-navy)]"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(category.intro_text) }} data-testid="blog-category-intro" />
          </div>
        </section>
      )}

      <section className="py-10 md:py-14 bg-[hsl(var(--muted))]/30">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[color:var(--brand-navy)] mb-8 text-center">
            {posts.length} article{posts.length > 1 ? "s" : ""} en {category.name}
          </h2>
          {posts.length === 0 ? (
            <Card className="p-10 text-center bg-white">
              <p className="text-[hsl(var(--muted-foreground))]">Aucun article dans cette catégorie pour le moment.</p>
              <Button asChild className="mt-4 bg-[color:var(--brand-navy)]"><Link to="/blog">Voir tous les articles</Link></Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts.map(p => (
                <Link key={p.id} to={`/blog/${p.slug || p.id}`} className="group" data-testid={`category-blog-card-${p.id}`}>
                  <Card className="overflow-hidden h-full transition-all hover:shadow-xl hover:-translate-y-1 bg-white">
                    <div className="aspect-video bg-slate-100 relative overflow-hidden">
                      {p.cover_image ? (
                        <img src={p.cover_image} alt={p.cover_alt || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">Pas d'image</div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-[color:var(--brand-navy)] group-hover:text-[color:var(--brand-sky)] transition-colors line-clamp-2">{p.title}</h3>
                      {p.excerpt && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.excerpt}</p>}
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                        {p.author && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{p.author}</span>}
                        {p.reading_time > 0 && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.reading_time} min</span>}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {Array.isArray(category.faq) && category.faq.length > 0 && (
        <section className="py-12 md:py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[color:var(--brand-navy)] mb-8 text-center">Questions fréquentes</h2>
            <div className="space-y-3">
              {category.faq.map((q, i) => (
                <details key={i} className="group bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid={`blog-category-faq-${i}`}>
                  <summary className="cursor-pointer px-5 py-4 font-semibold text-[color:var(--brand-navy)] flex items-center justify-between gap-2">
                    {q.question}
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-4 text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: markdownToHtml(q.answer || "") }} />
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {(category.cta_title || category.cta_link) && (
        <section className="py-12 md:py-16 bg-gradient-to-br from-[color:var(--brand-navy)] to-[color:var(--brand-sky)] text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{category.cta_title || "Un projet en tête ?"}</h2>
            <p className="text-white/90 mb-6 text-lg">Demandez votre devis personnalisé — réponse sous 24h.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button asChild size="lg" className="bg-white text-[color:var(--brand-navy)] hover:bg-white/90 font-bold">
                <Link to={category.cta_link || "/devis"}>Demander un devis<ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              {s?.phone && (
                <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <a href={`tel:${s.phone}`}><Phone className="w-4 h-4 mr-2" />{s.phone}</a>
                </Button>
              )}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function markdownToHtml(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = esc(md);
  html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline">$1</a>');
  html = html.replace(/^- (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>.+<\/li>\n?)+/g, m => `<ul>${m}</ul>`);
  html = html.replace(/\n\n/g, "</p><p>");
  return `<p>${html}</p>`;
}
